import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  InstitutionsForOccupationOptions,
  SchoolDetails,
  Institution
} from '../../models';

import {
  InstitutionsByOccupationGQL,
  InstitutionsByProgramGQL,
  QueryInstitutionsByProgramArgs,
  SchoolDetailsGQL
} from '@cpd-app/generated';
import {
  cipCodeCutoffFilterMapper,
  edLevelToEdLevelFilterMapper
} from '@cpd-app/core-module/mappers';

@Injectable({
  providedIn: 'root'
})
export class InstitutionDataService {
  constructor(
    private institutionsByProgramGQL: InstitutionsByProgramGQL,
    private institutionsByOccupationGQL: InstitutionsByOccupationGQL,
    private schoolDetailsGQL: SchoolDetailsGQL
  ) {}

  private mapInstitutionAddress(inst: any): Institution {
    return {
      ...inst,
      streetAddress1: inst.address.streetAddress1,
      streetAddress2: inst.address.streetAddress2,
      city: inst.address.city,
      state: inst.address.state,
      zip: inst.address.zip
    } as Institution;
  }

  /**
   * OC-2. One page of schools, and the size of the whole matched set.
   *
   * Returns the total alongside the rows so the pager can be drawn from one
   * request. Omitting options.limit still fetches everything, which is what
   * the other callers of this do.
   */
  public getInstitutionsForOccupation(
    onetCode: string,
    options: InstitutionsForOccupationOptions
  ): Observable<{ items: Institution[]; totalCount: number }> {
    return this.institutionsByOccupationGQL
      .fetch({
        onetCode,
        educationLevel: edLevelToEdLevelFilterMapper(+options.edLevelId),
        cipCodeCutoff: cipCodeCutoffFilterMapper(options.cipCodeCutoff),
        online: options.useOnlineFilter,
        openAdmission: options.useOpenAdmissionFilter,
        regionalAccreditation: options.useRegAccredFilter,
        avarageCost: options.avarageCost,
        medianDebt: options.medianDebt,
        graduationRate: options.graduationRate,
        searchTerm: options.searchTerm,
        taApproved: options.useTaApprovedFilter,
        limit: options.limit ?? null,
        offset: options.offset ?? 0,
        // OCC.5. The chosen credential; null keeps the occupation-wide list.
        schoolProgramId: options.schoolProgramId ?? null,
        // WALK-67 / WALK-66.
        state: options.state ?? null,
        allSchools: options.allSchools ?? false
      })
      .pipe(
        map((results) => {
          // institutionsByOccupation is NULLABLE on the schema, so the field
          // is guarded as well as the data: a null here used to throw inside
          // the map, which the effect turned into a load failure that the
          // table then rendered as "no schools match".
          const page = results?.data?.institutionsByOccupation;
          return {
            items: (page?.items ?? [])
              .filter(Boolean)
              .map((inst) => this.mapInstitutionAddress(inst)),
            totalCount: page?.totalCount ?? 0
          };
        })
      );
  }

  public getSchoolDetails(schoolId: string): Observable<SchoolDetails> {
    return this.schoolDetailsGQL.fetch({ schoolId }).pipe(
      map((results) => {
        const schoolDetails = results?.data?.schoolDetails;
        // schoolDetails is nullable on the schema. Reading through it threw
        // inside the observable and left the panel on its skeleton forever, so
        // a missing school is thrown as a failure the effect can report.
        if (!schoolDetails) {
          throw new Error(`No school details for schoolId ${schoolId}`);
        }
        const address = schoolDetails.address;
        return {
          ...schoolDetails,
          address: address
            ? `${address.streetAddress1} ${address.streetAddress2} ${address.city}, ${address.state} ${address.zip}`
            : ''
        } as any;
      })
    );
  }

  public getInstitutionsByProgram(
    input: Partial<QueryInstitutionsByProgramArgs>
  ): Observable<Institution[]> {
    return this.institutionsByProgramGQL
      .fetch({
        programCipCode: input.programCipCode || null,
        programName: input.programName || null,
        educationLevel: input.educationLevel || null,
        searchTerm: input.searchTerm || null,
        online: input.online || false,
        openAdmission: input.openAdmission || false,
        regionalAccreditation: input.regionalAccreditation || false
      })
      .pipe(
        map((results) =>
          // Nullable list, same as institutionsByOccupation above.
          (results?.data?.institutionsByProgram ?? []).map((inst) =>
            this.mapInstitutionAddress(inst)
          )
        )
      );
  }
}
